import React from "react";
import {
  Box,
  IconButton,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon,
  MoreVert as MoreVertIcon,
  PersonAdd as PersonAddIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon,
  VolumeOff as VolumeOffIcon,
  Report as ReportIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  LockOpen as LockOpenIcon,
  DeleteSweep as DeleteSweepIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { getImageDisplayUrl } from "src/network/endpoints";

// styled component for chat header with mobile sticky positioning
const ChatHeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  flexShrink: 0,
  // mobile-specific sticky positioning
  [theme.breakpoints.down("md")]: {
    position: "sticky",
    top: 0,
    zIndex: 1200, // Higher z-index to ensure it stays above other elements
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)", // Stronger shadow for better visibility
    // ensure header stays above content
    left: 0,
    right: 0,
    width: "100%",
    // add mobile-specific styling
    padding: theme.spacing(1.5, 2),
    minHeight: "64px",
    // prevent header from being hidden during scroll
    transform: "translateZ(0)",
    willChange: "transform",
    // ensure sticky positioning works properly
    position: "sticky",
    top: 0,
    // prevent any parent overflow from affecting sticky positioning
    isolation: "isolate",
    // ensure proper stacking context
    contain: "layout style paint",
  },
  // very small screen adjustments
  [theme.breakpoints.down("xs")]: {
    padding: theme.spacing(1, 1.5),
    minHeight: "56px",
  },
}));

// styled component for online status indicator
const OnlineStatus = styled(Box)(({ theme, isOnline }) => ({
  position: "absolute",
  bottom: 0,
  right: 0,
  width: 12,
  height: 12,
  borderRadius: "50%",
  backgroundColor: isOnline
    ? theme.palette.success.main
    : theme.palette.grey[400],
  border: `2px solid ${theme.palette.background.paper}`,
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
}));

/**
 * ChatHeader Component
 * 
 * This component displays the header section of a chat conversation including:
 * - Mobile back button (only visible on mobile)
 * - Contact avatar with online status
 * - Contact name and online/offline status
 * - Group info button (only for group conversations)
 * - Menu button with various chat options
 * 
 * @param {Object} props - Component props
 * @param {Object} props.selectedContact - The currently selected contact/conversation
 * @param {boolean} props.isConnected - Socket connection status
 * @param {Function} props.onClose - Function to handle closing the chat (mobile back button)
 * @param {Function} props.handleOpenGroupInfo - Function to open group info panel
 * @param {Function} props.handleMenuOpen - Function to open the menu
 * @param {Function} props.handleMenuClose - Function to close the menu
 * @param {Function} props.handleMenuAction - Function to handle menu item actions
 * @param {Element} props.menuAnchorEl - Anchor element for the menu positioning
 * @param {boolean} props.isMenuOpen - Whether the menu is currently open
 * @param {boolean} props.isOwnerOrTeamMember - Whether the current user is a business owner or team member
 */
const ChatHeader = ({
  selectedContact,
  isConnected,
  onClose,
  handleOpenGroupInfo,
  handleMenuOpen,
  handleMenuClose,
  handleMenuAction,
  menuAnchorEl,
  isMenuOpen,
  isOwnerOrTeamMember = false,
  conversationInfo = null,
  currentUserId = null,
  canViewOrder = false,
  canCloseOrder = false,
}) => {
  const navigate = useNavigate();

  const handleProfileClick = () => {
    if (!selectedContact.isGroup && selectedContact.oppositeUserId) {
      navigate(`/profile/${selectedContact.oppositeUserId}`);
    }
  };

  // Compute locally from conversationInfo since the prop from ChatView can be stale
  const isBusinessOwnerOrTeam =
    selectedContact.isGroup &&
    conversationInfo?.groupType === "business" &&
    conversationInfo?.users?.some(
      (u) => String(u?.userId?._id) === String(currentUserId) &&
        (u.userType === "owner" || u.userType === "teamMember")
    );

  const currentBusinessUserType =
    (selectedContact.isGroup && conversationInfo?.groupType === "business" && currentUserId
      ? (conversationInfo?.admin?.find((m) => String(m?.userId?._id) === String(currentUserId))?.userType ||
         conversationInfo?.users?.find((m) => String(m?.userId?._id) === String(currentUserId))?.userType)
      : null) || null;
  const canRateBusinessChat =
    selectedContact.isGroup &&
    conversationInfo?.groupType === "business" &&
    currentBusinessUserType === "user";

  // Check if conversation is muted by current user
  const isMuted = conversationInfo?.isMuteUser?.some(
    (muteUserId) => String(muteUserId) === String(currentUserId)
  );

  // Check if current user has exited or been removed from group
  const isExitedOrRemoved =
    !!currentUserId &&
    !!conversationInfo &&
    (() => {
      const s = String(currentUserId);
      const removed =
        Array.isArray(conversationInfo.removeUsersByAdmin) &&
        conversationInfo.removeUsersByAdmin.some((u) => String(u.userId) === s);
      if (removed) return true;
      const exited =
        Array.isArray(conversationInfo.exitUsers) &&
        conversationInfo.exitUsers.some((u) => String(u.userId) === s);
      return !!exited;
    })();

  const adminList = conversationInfo?.admin && Array.isArray(conversationInfo.admin) ? conversationInfo.admin : [];
  const isCurrentUserAdmin = !!currentUserId && adminList.some(
    (a) => String(a?.userId?._id) === String(currentUserId)
  );
  const adminCount = adminList.length;
  const hideExitForSoleAdmin = !isExitedOrRemoved && isCurrentUserAdmin && adminCount === 1;

  return (
    <ChatHeaderContainer className="chat-header-container">
      {/* mobile back button - only visible on mobile devices */}
      <IconButton
        size="small"
        onClick={onClose}
        sx={{
          display: { xs: "flex", md: "none" },
          padding: { xs: 0.5, sm: 1 },
          mr: 1,
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      {/* contact avatar with online status indicator */}
      <Box
        sx={{ position: "relative", cursor: !selectedContact.isGroup ? "pointer" : "default" }}
        onClick={handleProfileClick}
      >
        <Avatar
          sx={{
            width: { xs: 36, sm: 40 },
            height: { xs: 36, sm: 40 },
          }}
        >
          {selectedContact?.avatar ? (
            <img
              src={getImageDisplayUrl(selectedContact.avatar)}
              alt={selectedContact?.name}
            />
          ) : (
            selectedContact?.name.charAt(0).toUpperCase()
          )}
        </Avatar>
        <OnlineStatus isOnline={isConnected} />
      </Box>

      {/* contact information section */}
      <Box
        sx={{ flex: 1, cursor: !selectedContact.isGroup ? "pointer" : "default" }}
        onClick={handleProfileClick}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            fontSize: { xs: "0.875rem", sm: "1rem" },
          }}
        >
          {selectedContact.name}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontSize: { xs: "0.7rem", sm: "0.75rem" },
          }}
        >
          {isConnected ? "online" : "offline"}
        </Typography>
      </Box>

      {/* group info button - only show for group conversations; hide when user is removed from group */}
      {selectedContact.isGroup && !isExitedOrRemoved && (
        <IconButton
          size="small"
          onClick={handleOpenGroupInfo}
          title="group info"
        >
          <InfoIcon sx={{ color: "#4D0068" }} />
        </IconButton>
      )}

      {/* menu button to open chat options - hide when user is removed from group */}
      {!isExitedOrRemoved && (
        <>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{
              padding: { xs: 0.5, sm: 1 },
            }}
          >
            <MoreVertIcon />
          </IconButton>

          {/* chat options menu with various actions */}
          <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 200,
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            border: "1px solid #E5E7EB",
          },
        }}
      >
        {/* ONE-TO-ONE */}
        {!selectedContact.isGroup && <>
          <MenuItem disabled sx={{ opacity: 0.6 }}>
            <ListItemIcon><BlockIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
            <Typography variant="body2" sx={{ color: "#374151" }}>block</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleMenuAction("mute")}>
            <ListItemIcon><VolumeOffIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
            <Typography variant="body2" sx={{ color: "#374151" }}>{isMuted ? "unmute" : "mute"}</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleMenuAction("report")}>
            <ListItemIcon><ReportIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
            <Typography variant="body2" sx={{ color: "#374151" }}>report issue</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleMenuAction("clearChat")}>
            <ListItemIcon><DeleteSweepIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
            <Typography variant="body2" sx={{ color: "#374151" }}>clear chat</Typography>
          </MenuItem>
        </>}

        {/* BUSINESS CHAT - OWNER/TEAM MEMBER */}
        {isBusinessOwnerOrTeam && <>
          <MenuItem onClick={() => handleMenuAction("addMember")}>
            <ListItemIcon><PersonAddIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
            <Typography variant="body2" sx={{ color: "#374151" }}>add member</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => handleMenuAction("viewOrder")}
            disabled={!canViewOrder}
            sx={!canViewOrder ? { opacity: 0.6 } : undefined}
          >
            <ListItemIcon><ScheduleIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
            <Typography variant="body2" sx={{ color: "#374151" }}>view order</Typography>
          </MenuItem>
          {canRateBusinessChat && (
            <MenuItem onClick={() => handleMenuAction("rate")}>
              <ListItemIcon><StarIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
              <Typography variant="body2" sx={{ color: "#374151" }}>rate</Typography>
            </MenuItem>
          )}
          {canCloseOrder && (
            <MenuItem onClick={() => handleMenuAction("closeOrder")}>
              <ListItemIcon><CloseIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
              <Typography variant="body2" sx={{ color: "#374151" }}>closed order</Typography>
            </MenuItem>
          )}
          <MenuItem onClick={() => handleMenuAction("mute")}>
            <ListItemIcon><VolumeOffIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
            <Typography variant="body2" sx={{ color: "#374151" }}>{isMuted ? "unmute" : "mute"}</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleMenuAction("report")}>
            <ListItemIcon><ReportIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
            <Typography variant="body2" sx={{ color: "#374151" }}>report issue</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleMenuAction("closeConversation")}>
            <ListItemIcon>
              {conversationInfo?.isOpen === false
                ? <LockOpenIcon sx={{ fontSize: 20, color: "#6B7280" }} />
                : <CloseIcon sx={{ fontSize: 20, color: "#6B7280" }} />}
            </ListItemIcon>
            <Typography variant="body2" sx={{ color: "#374151" }}>
              {conversationInfo?.isOpen === false ? "open conversation" : "close conversation"}
            </Typography>
          </MenuItem>
          <MenuItem disabled sx={{ opacity: 0.6 }}>
            <ListItemIcon><SettingsIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
            <Typography variant="body2" sx={{ color: "#374151" }}>budget setting</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleMenuAction("clearChat")}>
            <ListItemIcon><DeleteSweepIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
            <Typography variant="body2" sx={{ color: "#374151" }}>clear chat</Typography>
          </MenuItem>
        </>}

        {/* BUSINESS CHAT - CUSTOMER */}
        {selectedContact.isGroup && conversationInfo?.groupType === "business" && !isBusinessOwnerOrTeam && <>
          <MenuItem onClick={() => handleMenuAction("addMember")}>
            <ListItemIcon><PersonAddIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
            <Typography variant="body2" sx={{ color: "#374151" }}>add member</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => handleMenuAction("viewOrder")}
            disabled={!canViewOrder}
            sx={!canViewOrder ? { opacity: 0.6 } : undefined}
          >
            <ListItemIcon><ScheduleIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
            <Typography variant="body2" sx={{ color: "#374151" }}>view order</Typography>
          </MenuItem>
          {canRateBusinessChat && (
            <MenuItem onClick={() => handleMenuAction("rate")}>
              <ListItemIcon><StarIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
              <Typography variant="body2" sx={{ color: "#374151" }}>rate</Typography>
            </MenuItem>
          )}
          <MenuItem onClick={() => handleMenuAction("report")}>
            <ListItemIcon><ReportIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
            <Typography variant="body2" sx={{ color: "#374151" }}>report issue</Typography>
          </MenuItem>
          <MenuItem disabled sx={{ opacity: 0.6 }}>
            <ListItemIcon><SettingsIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
            <Typography variant="body2" sx={{ color: "#374151" }}>budget setting</Typography>
          </MenuItem>
        </>}

        {/* NON-BUSINESS GROUP */}
        {selectedContact.isGroup && conversationInfo?.groupType !== "business" && <>
          <MenuItem onClick={() => handleMenuAction("addMember")}>
            <ListItemIcon><PersonAddIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
            <Typography variant="body2" sx={{ color: "#374151" }}>add member</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleMenuAction("mute")}>
            <ListItemIcon><VolumeOffIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
            <Typography variant="body2" sx={{ color: "#374151" }}>{isMuted ? "unmute" : "mute"}</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleMenuAction("report")}>
            <ListItemIcon><ReportIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
            <Typography variant="body2" sx={{ color: "#374151" }}>report issue</Typography>
          </MenuItem>
          {isCurrentUserAdmin && (
            <MenuItem onClick={() => handleMenuAction("deleteGroup")}>
              <ListItemIcon><DeleteIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
              <Typography variant="body2" sx={{ color: "#374151" }}>delete group</Typography>
            </MenuItem>
          )}
          <MenuItem onClick={() => handleMenuAction("clearChat")}>
            <ListItemIcon><DeleteSweepIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
            <Typography variant="body2" sx={{ color: "#374151" }}>clear chat</Typography>
          </MenuItem>
        </>}
      </Menu>
        </>
      )}
    </ChatHeaderContainer>
  );
};

export default ChatHeader;
